/* eslint-disable @typescript-eslint/no-unused-vars */
import { Row, RowData } from "@tanstack/react-table";
import { Table, TableFeature } from "@tanstack/table-core";

// define types for our new feature's custom state
export type RowClassNameState = {
  className?: string;
};

export interface RowClassNameTableState {}

// define types for our new feature's table options
export interface RowClassNameOptions<TData> {
  getRowClassName?: (row: Row<TData>) => string | undefined;
}

// Define types for our new feature's table APIs
export interface RowClassNameInstance<TData> {
  getRowClassName: (row: Row<TData>) => string | undefined;
}

export interface RowClassNameRow {
  getClassName: () => string | undefined;
}

// Use declaration merging to add our new feature APIs and state types to TanStack Table's existing types.
declare module "@tanstack/react-table" {
  //merge our new feature's state with the existing table state
  interface TableState extends RowClassNameTableState {}
  //merge our new feature's options with the existing table options
  interface TableOptionsResolved<TData extends RowData>
    extends RowClassNameOptions<TData> {}
  //merge our new feature's instance APIs with the existing table instance APIs
  interface Table<TData extends RowData> extends RowClassNameInstance<TData> {}
  // if you need to add cell instance APIs...
  // interface Cell<TData extends RowData, TValue> extends RowClassNameCell
  // if you need to add row instance APIs...
  interface Row<TData extends RowData> extends RowClassNameRow {}
  // if you need to add column instance APIs...
  // interface Column<TData extends RowData, TValue> extends RowClassNameColumn
  // if you need to add header instance APIs...
  // interface Header<TData extends RowData, TValue> extends RowClassNameHeader

  // Note: declaration merging on `ColumnDef` is not possible because it is a type, not an interface.
  // But you can still use declaration merging on `ColumnDef.meta`
}

// end of TS setup!

// Here is all of the actual javascript code for our new feature
export const RowClassNameFeature: TableFeature<any> = {
  // define the new feature's initial state
  getInitialState: (state): RowClassNameTableState => {
    return {
      ...state,
    };
  },

  // define the new feature's default options
  getDefaultOptions: <TData extends RowData>(
    table: Table<TData>,
  ): RowClassNameOptions<TData> => {
    return {
      getRowClassName: undefined,
    };
  },
  // if you need to add a default column definition...
  // getDefaultColumnDef: <TData extends RowData>(): Partial<ColumnDef<TData>> => {
  //   return { meta: {} } //use meta instead of directly adding to the columnDef to avoid typescript stuff that's hard to workaround
  // },

  // define the new feature's table instance methods
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.getRowClassName = (row) => {
      return row.getClassName();
    };
  },

  // if you need to add row instance APIs...
  createRow: <TData extends RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ): void => {
    row.getClassName = () =>
      table.options.getRowClassName
        ? table.options.getRowClassName(row)
        : undefined;
  },
  // if you need to add cell instance APIs...
  // createCell: <TData extends RowData>(cell, column, row, table): void => {},
  // if you need to add column instance APIs...
  // createColumn: <TData extends RowData>(column, table): void => {},
  // if you need to add header instance APIs...
  // createHeader: <TData extends RowData>(header, table): void => {},
};
//end of custom feature code
